'use client';

import { GET_ITEM_COUNT_QUERY } from '@/components/Header/cartTrigger.gql';
import { MINI_CART_QUERY } from '@/components/MiniCart/miniCart.gql';
import { mittInstance } from '@/constants/mitt-instance';
import { useQuery } from '@apollo/client';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useCartContext } from './cart';

const MiniCartContext = createContext({
  count: 0,
  miniCartData: null,
  miniCartDataLoading: false,
  setShouldQueryMiniCart: (state: boolean) => {},
});

export function MiniCartInfoProvider({ children }: { children: React.ReactNode }) {
  const [{ cartId }] = useCartContext();

  const [shouldQueryMiniCart, setShouldQueryMiniCart] = useState(false);

  const { data, refetch } = useQuery(GET_ITEM_COUNT_QUERY, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    context: {
      headers: {
        'X-Robots-Tag': 'noindex, nofollow',
      },
    },
    variables: {
      cartId,
    },
    skip: !cartId,
  });

  const { data: miniCartData, loading: miniCartLoading } = useQuery(MINI_CART_QUERY, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      cartId,
    },
    skip: !cartId || !shouldQueryMiniCart,
    errorPolicy: 'all',
    context: {
      headers: {
        'X-Robots-Tag': 'noindex, nofollow',
      },
    },
  });

  useEffect(() => {
    const handler = () => {
      refetch();
    };

    mittInstance.on('REFRESH_CART_TRIGGER_ITEM_COUNT', handler);

    return () => {
      mittInstance.off('REFRESH_CART_TRIGGER_ITEM_COUNT', handler);
    };
  }, [cartId, refetch]);

  const itemCount = data?.cart?.total_quantity || 0;

  const contextValue = useMemo(() => {
    return {
      count: itemCount,
      miniCartData,
      miniCartDataLoading: miniCartLoading,
      setShouldQueryMiniCart,
    };
  }, [itemCount, miniCartData, miniCartLoading, setShouldQueryMiniCart]);

  return <MiniCartContext.Provider value={contextValue}>{children}</MiniCartContext.Provider>;
}

export function useMiniCartInfo() {
  const context = useContext(MiniCartContext);

  if (context === undefined) {
    throw new Error('useMiniCartInfo must be used within an MiniCartInfoProvider');
  }

  return context;
}
